import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import countBy from 'lodash/countBy';
import get from 'lodash/get';
import orderBy from 'lodash/orderBy';
import { Observable, BehaviorSubject, combineLatest } from 'rxjs';
import { debounceTime, filter, first, map, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';

import { ConfigService } from '../../config.service';
import { ConfirmDialog, DialogService } from '../../dialogs';
import { ApiParams, CoreApiService, queryAll } from '../../core-api.service';
import { JobLogDialog } from '../job-log/job-log-dialog.component';
import { LoggingService } from '../../logging.service';
import { NotificationService } from '../../notification.service';
import { ProfileService } from '../../profile/profile.service';
import { ProjectsService } from '../../projects/projects.service';
import { getContainerType, getContainerProjectId, getRoleMatrix } from '../../shared/container.utils';
import { ContainerAccess } from '../../shared/enums/container-access.enum';
import { JobState } from '../../shared/enums/job-state.enum';
import { PermissionAction } from '../../shared/enums/permission-action.enum';
import { Container } from '../../shared/models/container.model';
import { File } from '../../shared/models/file.model';
import { Job, JobInput } from '../../shared/models/job.model';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { FwIconModule } from '@flywheel-io/vision';
import { DirectivesModule } from 'app/src/common/directives.module';
import { ContainerModule } from 'app/src/containers/container.module';
import { FileModule } from 'app/src/files/file.module';
import { MaterialModule } from 'app/src/material.module';
import { PipesModule } from 'app/src/shared/pipes.module';
import { SharedModule } from 'app/src/shared/shared.module';

interface JobSummary {
  complete: number
  failed: number
  pending: number
  running: number
  total: number
}

interface JobInputExt extends JobInput {
  tooltip: string
}

interface FileExt extends File {
  tooltip: string
}

interface JobExt extends Job {
  name: string
  inputLimit: number
  inputList: JobInputExt[]
  outputLimit: number
  outputList: FileExt[]
}

@Component({
  selector: 'job-provenance',
  templateUrl: './job-provenance.component.html',
  styleUrls: ['./job-provenance.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ContainerModule,
    DirectivesModule,
    FileModule,
    FormsModule,
    FwIconModule,
    MaterialModule,
    MatTabsModule,
    PipesModule,
    ReactiveFormsModule,
    SharedModule,
  ],
})
export class JobProvenanceComponent implements OnChanges {
  @Input() container: Container
  @Input() pageSize = 50

  allJobs$: Observable<Job[]>
  gearNameFilter = new FormControl()
  isAdmin = false
  jobPrioritization = this.config.isFeatureEnabled('job_prioritization')
  jobs$: Observable<JobExt[]>
  summary$: Observable<JobSummary>
  totalJobs = 0

  private page$: BehaviorSubject<number>

  constructor(
    private config: ConfigService,
    private dialog: DialogService,
    private logger: LoggingService,
    private notifications: NotificationService,
    private coreApi: CoreApiService,
    private profileService: ProfileService,
    private projectsService: ProjectsService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.container && this.container) {
      const { currentValue, previousValue } = changes.container;
      if (!this.jobs$ || currentValue._id !== previousValue._id) {
        this.initStreams();
      }
      this.updatePermissions();
    }
  }

  /**
   * Set up the jobs$ stream and fetch the first page of jobs. If page$ is updated,
   * the next page is fetched and appended to the result, building a growing list of jobs.
   */
  private initStreams(): void {
    const containerType = getContainerType(this.container);
    this.page$ = new BehaviorSubject(1);
    this.allJobs$ = this.gearNameFilter.valueChanges.pipe(
      startWith(''),
      debounceTime(200),
      switchMap((gearName) => {
        const params: ApiParams = {
          sort: 'created:desc',
        };
        const filters = [];
        if (gearName) {
          filters.push(`gear_info.name=~${gearName}`);
        }
        filters.push(`parents.${containerType}=${this.container._id}`);
        if (filters.length > 0) {
          params.filter = filters.join(',');
        }
        return queryAll(() => this.coreApi.projects.jobs(this.container.parents.project, params));
      }),
      tap((jobs) => {
        this.totalJobs = jobs.length;
      }),
    );
    this.jobs$ = combineLatest([
      this.page$,
      this.allJobs$,
    ]).pipe(
      map(([page, allJobs]) => {
        const jobs = allJobs.slice(0, this.pageSize * page).map(job => {
          const inputList = (job.inputs ?? [])
            .filter(input => input.name?.length > 0)
            .map(input => {
              const inputConfig = get(job, ['config', 'inputs', input.input, 'object']) ?? {};
              const tooltip = [`Name: ${input.name}`];
              if (inputConfig.origin) {
                tooltip.push(
                  `Origin Type: ${inputConfig.origin.type}`,
                  `Origin: ${inputConfig.origin.id}`,
                );
              }
              if (inputConfig.version) {
                tooltip.push(`Version: ${inputConfig.version}`);
              }
              return {
                ...input,
                tooltip: tooltip.join('\n'),
                ...(inputConfig.file_id ? { file: inputConfig } : {}),
              };
            });
          const outputList = (job.outputs ?? job.saved_files?.map(name => ({ name }) as File) ?? [])
            .map(output => {
              const tooltip = [`Name: ${output.name}`];
              if (output.version) {
                tooltip.push(`Version: ${output.version}`);
              }
              return {
                ...output,
                tooltip: tooltip.join('\n'),
              };
            });
          return {
            ...job,
            inputLimit: 1,
            inputList,
            name: `${job.gear_info.name} v${job.gear_info.version}`,
            outputLimit: 1,
            outputList,
          };
        });
        return orderBy(jobs, ['created'], ['desc']) as JobExt[];
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.summary$ = this.allJobs$.pipe(
      map(jobs => {
        const { failed, pending, running, complete } = countBy(jobs, 'state');
        return {
          complete: complete || 0,
          failed: failed || 0,
          pending: pending || 0,
          running: running || 0,
          total: jobs.length,
        };
      }),
    );
  }

  refreshJobs(): void {
    this.initStreams();
  }

  nextPage(): void {
    this.page$.next(this.page$.value + 1);
  }

  retryJob(job: Job): void {
    this.coreApi.jobs.retry(job._id).subscribe({
      next: () => {
        this.notifications.success('jobs.retry.success');
        this.refreshJobs();
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('jobs.retry.error');
      },
    });
  }

  cancelJob(job: Job): void {
    this.dialog.open(ConfirmDialog, {
      data: {
        content: 'Are you sure you want to cancel the job?',
      },
    }).afterClosed().pipe(
      filter(Boolean),
      switchMap(() => this.coreApi.jobs.put(job._id, { state: JobState.Cancelled })),
    ).subscribe({
      next: () => {
        this.notifications.success('jobs.cancel.success');
        this.refreshJobs();
      },
      error: error => {
        this.logger.error(error);
        this.notifications.error('jobs.cancel.error');
      },
    });
  }

  displayJobLog(job: JobExt): void {
    this.dialog.open(JobLogDialog, {
      data: { jobId: job._id, jobName: job.name },
      disableClose: false,
      width: '900px',
    });
  }

  updatePermissions(): void {
    const projectId = getContainerProjectId(this.container);
    if (projectId) {
      this.projectsService.getProjectPermissions(projectId).subscribe(projectPermissions => {
        this.isAdmin = projectPermissions.includes(
          this.config.isFeatureEnabled('job_prioritization')
            ? PermissionAction.JobsManage
            : PermissionAction.JobsRunCancel
        );
      });
    } else {
      // Fall back to roleMatrix if the container doesn't have roles
      this.profileService.getProfile().pipe(first()).subscribe(profile => {
        const roleMatrix = getRoleMatrix(this.container, profile);
        this.isAdmin = roleMatrix[ContainerAccess.Admin];
      });
    }
  }
}
